import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import { Printer, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Print-optimized A4 sticker sheet: 24 identical mini-QRs per page (4 cols × 6 rows).
 * Designed to print on Avery 5163 / any standard 63×38mm shipping-label sheet.
 * Each sticker overlays the dead QR on the already-printed tri-fold brochure.
 */
export default function BrochureStickers() {
  const [cta, setCta] = useState(null);
  const [lang, setLang] = useState('sw');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/api/whatsapp/cta?lang=${lang}`);
        setCta(data);
      } catch (_) {
        setCta(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [lang]);

  // 24 stickers per A4 page
  const stickers = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);

  const microLabel = lang === 'sw' ? 'Bofya tuma AI ikujibu' : 'Tap send — AI replies';

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Toolbar - hidden on print */}
      <div className="print:hidden bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
          <Link
            to="/admin"
            className="text-sm text-slate-600 hover:text-emerald-700 flex items-center gap-1"
            data-testid="stickers-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to admin
          </Link>
          <h1 className="text-base font-semibold text-slate-900">
            WhatsApp AI — Brochure sticker sheet
          </h1>
          <div className="flex items-center gap-2">
            {['sw', 'en'].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`h-8 px-3 rounded-lg text-xs font-medium border ${
                  lang === l
                    ? 'bg-emerald-700 text-white border-emerald-700'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-600'
                }`}
                data-testid={`stickers-lang-${l}`}
              >
                {l === 'sw' ? 'Kiswahili' : 'English'}
              </button>
            ))}
            <button
              onClick={() => window.print()}
              disabled={!cta}
              className="h-9 px-4 rounded-lg bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:opacity-60 flex items-center gap-2"
              data-testid="stickers-print-btn"
            >
              <Printer className="w-4 h-4" /> Print sheet
            </button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 pb-3 text-xs text-slate-500">
          Print on <strong>A4 sticker label sheet</strong> (Avery 5163 or similar, 63×38 mm).
          Cut and paste one sticker over each dead QR on the tri-fold brochure.
          24 stickers per page.
        </div>
      </div>

      {/* A4 sticker page */}
      <div className="flex justify-center py-6 print:py-0">
        <div
          className="bg-white shadow-lg print:shadow-none sticker-page"
          style={{
            width: '210mm',
            minHeight: '297mm',
            padding: '10mm',
            boxSizing: 'border-box',
          }}
        >
          {loading || !cta ? (
            <div className="h-[277mm] flex items-center justify-center text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading QR…
            </div>
          ) : (
            <div
              className="grid gap-[3mm]"
              style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}
            >
              {stickers.map((i) => (
                <div
                  key={i}
                  className="border border-dashed border-slate-300 rounded flex flex-col items-center justify-center text-center"
                  style={{ height: '46mm', padding: '2mm' }}
                >
                  <QRCodeCanvas
                    value={`${window.location.origin}/ai?lang=${lang}`}
                    size={110}
                    level="M"
                    includeMargin={false}
                  />
                  <div
                    className="mt-1 font-semibold text-slate-900 leading-tight"
                    style={{ fontSize: '7pt' }}
                  >
                    Wekeza-Smart AI 🇹🇿
                  </div>
                  <div className="text-slate-600 leading-tight" style={{ fontSize: '6pt' }}>
                    {microLabel}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          body { background: #fff !important; }
          .sticker-page { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
